import { useState, useMemo } from 'react'
import { useClient, useQuery } from '@based/sdk/react'
import Column from '@app/components/layout/Column'
import { Breadcrumbs } from '@app/components/core/breadcrumbs/Breadcrumbs'
import { InitiativeViewPageLayout } from '@app/pages/layout/InitiativeViewPageLayout'
import { useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { EventCard } from '@app/components/blocks/event/EventCard'
import type { DropdownMenuItem } from '@app/components/core/dropdown/DropdownMenu'
import { Modal } from '@app/components/blocks/modal/Modal'
import { useToast } from '@app/util/hooks/useToast'
import type { ListInitiativeAdminEventsModel } from 'functions/app/event/list-initiative-admin'
import { eventCreationSteps } from '@app/pages/events/create/shared/workflow'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'

type EventItem = ListInitiativeAdminEventsModel['events'][number]

export const InitiativeEvents = ({
  initiativeId,
}: {
  initiativeId: string
}) => {
  const translations = useTranslations().app.dashboard.initiative
  const client = useClient()
  const { showFailToast } = useToast()
  const [deleteTarget, setDeleteTarget] = useState<EventItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const eventsQuery = useQuery('list-initiative-event-admin', {
    initiativeId: Number(initiativeId),
  })
  const events: EventItem[] = eventsQuery.data?.events ?? []

  const handleDeleteEvent = async () => {
    if (!deleteTarget) {
      return
    }
    setIsDeleting(true)
    try {
      await client.call('delete-event', { id: deleteTarget.id })
      setDeleteTarget(null)
    } catch {
      showFailToast()
    } finally {
      setIsDeleting(false)
    }
  }

  const now = Date.now()
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)

  const isPastEvent = (event: EventItem) =>
    event.start < startOfToday.getTime() && event.end !== 0 && event.end < now

  const [draftEvents, upcomingEvents, pastEvents] = useMemo(() => {
    const sortedEvents = [...events].sort((a, b) => b.start - a.start)
    const draftEvents = sortedEvents.filter((event) => !event.published)
    const publishedEvents = sortedEvents.filter((event) => event.published)
    const upcomingEvents = publishedEvents.filter(
      (event) => !isPastEvent(event),
    )
    const pastEvents = publishedEvents.filter(isPastEvent)

    return [draftEvents, upcomingEvents, pastEvents]
  }, [events])

  const renderEvent = (event: EventItem) => {
    const startDate = new Date(event.start)
    const to = event.published
      ? pages.event(event.slug)
      : pages.eventCreateStep(event.slug, eventCreationSteps.generalInformation)

    const moreMenuItems: DropdownMenuItem[] = [
      {
        id: 'delete',
        label: translations.events.delete,
        linkProps: { onClick: () => setDeleteTarget(event) },
      },
    ]
    if (event.published) {
      moreMenuItems.push({
        id: 'edit',
        label: translations.events.edit,
        linkProps: {
          to: pages.eventCreateStep(
            event.slug,
            eventCreationSteps.generalInformation,
          ),
        },
      })
    }

    return (
      <EventCard
        key={event.uuid}
        start={startDate}
        end={event.end !== 0 ? new Date(event.end) : undefined}
        image={event.images?.[0]?.src ?? defaultProjectPicture}
        title={event.title}
        to={to}
        moreMenuItems={moreMenuItems}
      />
    )
  }

  return (
    <InitiativeViewPageLayout>
      <Column alignItems="stretch" gap="1.5rem" style={{ flexGrow: 0 }}>
        <Breadcrumbs
          levels={[
            {
              label: translations.common.myInitiatives,
              to: pages.dashboardInitiatives,
            },
            {
              label: translations.common.myInitiatives,
              to: pages.dashboardViewInitiative(initiativeId),
            },
            {
              label: translations.sideBar.events,
            },
          ]}
        />
        <Title.H3>{translations.sideBar.events}</Title.H3>
        {events?.length === 0 ? (
          <Text.Medium color="var(--palette-blue-600)">
            {translations.events.noEvents}
          </Text.Medium>
        ) : (
          <>
            {draftEvents?.length > 0 && (
              <>
                <Title.H3 style={{ marginBottom: 0 }}>
                  {translations.events.draftEvents}
                </Title.H3>
                {draftEvents.map(renderEvent)}
              </>
            )}

            {upcomingEvents?.length > 0 && (
              <>
                <Title.H3 style={{ marginBottom: 0 }}>
                  {translations.events.upcomingEvents}
                </Title.H3>
                {upcomingEvents.map(renderEvent)}
              </>
            )}

            {pastEvents?.length > 0 && (
              <>
                <Title.H3 style={{ marginBottom: 0 }}>
                  {translations.events.pastEvents}
                </Title.H3>
                {pastEvents.map(renderEvent)}
              </>
            )}
          </>
        )}
      </Column>
      <Modal
        isOpen={deleteTarget !== null}
        confirmLabel={translations.events.delete}
        loading={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleDeleteEvent()}
      >
        <Text.Medium>{translations.events.deleteConfirmation}</Text.Medium>
      </Modal>
    </InitiativeViewPageLayout>
  )
}
